import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { getConfiguredAuth0Client } from '../services/auth0-client';

const userRole = ref('standard');
const isLoadingRole = ref(false);
let roleRequest = null;

function extractRole(payload = {}) {
  const roles = payload['https://terrasense.io/roles'] || payload.roles ||
    payload['https://terrasense.io/app_metadata']?.roles || payload.app_metadata?.roles || [];
  if (Array.isArray(roles)) {
    if (roles.includes('admin')) return 'admin';
    if (roles.includes('support')) return 'support';
  }
  return 'standard';
}

function decodePayload(token) {
  if (!token) return {};
  const value = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
  if (!value) return {};
  return JSON.parse(decodeURIComponent(atob(value).split('').map(char => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`).join('')));
}

export function useUserRole() {
  const inSetup = Boolean(getCurrentInstance());
  const auth0 = (inSetup ? useAuth0() : null) || getConfiguredAuth0Client();
  if (!auth0) {
    throw new Error('Auth0 is not configured. Install the Auth0 plugin before loading user roles.');
  }
  const { getAccessTokenSilently, isAuthenticated, user } = auth0;

  const fetchUserRole = async () => {
    if (!isAuthenticated.value) { userRole.value = 'standard'; return userRole.value; }
    if (roleRequest) return roleRequest;
    isLoadingRole.value = true;
    roleRequest = (async () => {
      try {
        const token = await getAccessTokenSilently({ detailedResponse: true });
        const payload = decodePayload(token.id_token || token.access_token);
        userRole.value = extractRole(Object.keys(payload).length ? payload : user.value);
      } catch (_) {
        userRole.value = extractRole(user.value);
      } finally {
        isLoadingRole.value = false;
        roleRequest = null;
      }
      return userRole.value;
    })();
    return roleRequest;
  };

  if (inSetup) {
    watch(isAuthenticated, authenticated => {
      if (authenticated) fetchUserRole();
      else userRole.value = 'standard';
    }, { immediate: true });
  }

  return {
    userRole,
    isLoadingRole,
    isAdmin: computed(() => userRole.value === 'admin'),
    isSupport: computed(() => userRole.value === 'support'),
    isStandard: computed(() => userRole.value === 'standard'),
    fetchUserRole,
  };
}
